import { createContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { useLocation } from '@tanstack/react-router';
import { UserPreferences, ReaderLayout } from '../types/api';
import {
  FontMode,
  WidthMode,
  ThemeColor,
  ThemeSize,
  ReaderSidebarMode,
  getThemeBodyClass,
  getSizeBodyClass,
  getWidthBodyClass,
  getFontBodyClass,
} from '../types/theme';

// Sidebar navigation preferences
interface SidebarNavPreferences {
  sections: {
    [key: string]: boolean; // section name -> isOpen
  };
}

// Preferences State structure
interface UserPreferencesState {
  // Persisted preferences (saved to localStorage)
  preferences: UserPreferences;

  // UI-only state (not persisted)
  ui: {
    showReaderSidebar: boolean;
    readerSidebarMode: ReaderSidebarMode | null;
    showMobileSidebarNav: boolean;
  };
}

// Action types for reducer
type UserPreferencesAction =
  | { type: 'SET_BOOKMARK_LIST_VIEW'; payload: 'condensed' | 'standard' }
  | { type: 'SET_BOOKMARK_LIST_THUMBNAILS'; payload: boolean }
  | { type: 'SET_BOOKMARK_LIST_SORT_ORDER'; payload: 'newest' | 'oldest' }
  | { type: 'DISMISS_PREMIUM_NOTICE' }
  | { type: 'SET_BOOKMARK_LIST_COLUMN_WIDTH'; payload: number | null }
  | { type: 'SET_READER_BOOKMARK_ID'; payload: string | null }
  | { type: 'SET_READER_LAYOUT_MODE'; payload: ReaderLayout }
  | { type: 'SET_THEME_COLOR'; payload: ThemeColor }
  | { type: 'SET_THEME_FONT'; payload: FontMode }
  | { type: 'SET_THEME_SIZE'; payload: ThemeSize }
  | { type: 'SET_THEME_WIDTH'; payload: WidthMode }
  | { type: 'SET_THEME_WPM'; payload: number }
  | {
      type: 'SET_SIDEBAR_SECTION_OPEN';
      payload: { sectionName: string; isOpen: boolean };
    }
  | { type: 'SET_SHOW_READER_SIDEBAR'; payload: boolean }
  | { type: 'SET_READER_SIDEBAR_MODE'; payload: ReaderSidebarMode | null }
  | { type: 'SET_SHOW_MOBILE_SIDEBAR_NAV'; payload: boolean }
  | { type: 'TOGGLE_READER_SIDEBAR' }
  | { type: 'CLOSE_READER_SIDEBAR' }
  | { type: 'TOGGLE_MOBILE_SIDEBAR_NAV' }
  | { type: 'CLOSE_MOBILE_SIDEBAR_NAV' };

// Unified user preferences context interface
interface UserPreferencesContextType {
  // User preferences
  preferences: UserPreferences;
  setBookmarkListView: (value: 'condensed' | 'standard') => void;
  setBookmarkListThumbnails: (value: boolean) => void;
  setBookmarkListSortOrder: (value: 'newest' | 'oldest') => void;
  dismissPremiumNotice: () => void;

  // Theme settings
  color: ThemeColor;
  font: FontMode;
  size: ThemeSize;
  width: WidthMode;
  wpm: number;

  // Theme setters
  setColor: (color: ThemeColor) => void;
  setFont: (font: FontMode) => void;
  setSize: (size: ThemeSize) => void;
  setWidth: (width: WidthMode) => void;
  setWpm: (wpm: number) => void;

  // Sidebar management (reader sidebar)
  showReaderSidebar: boolean;
  readerSidebarMode: ReaderSidebarMode | null;
  setShowReaderSidebar: (show: boolean) => void;
  setReaderSidebarMode: (mode: ReaderSidebarMode | null) => void;

  // Utility functions
  toggleReaderSidebar: () => void;
  closeReaderSidebar: () => void;

  // Mobile navigation sidebar
  showMobileSidebarNav: boolean;
  setShowMobileSidebarNav: (show: boolean) => void;
  toggleMobileSidebarNav: () => void;
  closeMobileSidebarNav: () => void;

  // Navigation sidebar preferences
  sidebarNavPreferences: SidebarNavPreferences;
  setSidebarNavSectionOpen: (sectionName: string, isOpen: boolean) => void;

  // Bookmark list column width (reader column takes the rest)
  bookmarkListColumnWidth: number | null;
  setBookmarkListColumnWidth: (width: number | null) => void;

  // Last opened reader bookmark id (persists across route changes)
  readerBookmarkId: string | null;
  setReaderBookmarkId: (id: string | null) => void;

  // Reader layout: 2col (list only), full (reader only), or 3col (list + reader)
  readerLayoutMode: ReaderLayout;
  setReaderLayoutMode: (mode: ReaderLayout) => void;
}

// Reducer function
function userPreferencesReducer(
  state: UserPreferencesState,
  action: UserPreferencesAction,
): UserPreferencesState {
  switch (action.type) {
    case 'SET_BOOKMARK_LIST_VIEW':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          bookmarkList: {
            ...state.preferences.bookmarkList,
            view: action.payload,
          },
        },
      };

    case 'SET_BOOKMARK_LIST_THUMBNAILS':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          bookmarkList: {
            ...state.preferences.bookmarkList,
            thumbnails: action.payload,
          },
        },
      };

    case 'SET_BOOKMARK_LIST_SORT_ORDER':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          bookmarkList: {
            ...state.preferences.bookmarkList,
            sortOrder: action.payload,
          },
        },
      };

    case 'DISMISS_PREMIUM_NOTICE':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          premiumNotice: {
            dismissedAt: Date.now(),
          },
        },
      };

    case 'SET_BOOKMARK_LIST_COLUMN_WIDTH': {
      const updatedBookmarkList = { ...state.preferences.bookmarkList };
      if (action.payload === null) {
        delete updatedBookmarkList.columnWidth;
      } else {
        updatedBookmarkList.columnWidth = action.payload;
      }
      return {
        ...state,
        preferences: {
          ...state.preferences,
          bookmarkList: updatedBookmarkList,
        },
      };
    }

    case 'SET_READER_BOOKMARK_ID': {
      const updatedReader = state.preferences.reader
        ? { ...state.preferences.reader }
        : {};
      if (action.payload === null) {
        delete updatedReader.bookmarkId;
        // If reader object is empty, remove it
        if (Object.keys(updatedReader).length === 0) {
          const { reader: _reader, ...rest } = state.preferences;
          return {
            ...state,
            preferences: rest,
          };
        }
      } else {
        updatedReader.bookmarkId = action.payload;
      }
      return {
        ...state,
        preferences: {
          ...state.preferences,
          reader: updatedReader,
        },
      };
    }

    case 'SET_READER_LAYOUT_MODE': {
      const updatedReader = state.preferences.reader
        ? { ...state.preferences.reader }
        : {};
      updatedReader.layout = action.payload;
      return {
        ...state,
        preferences: {
          ...state.preferences,
          reader: updatedReader,
        },
      };
    }

    case 'SET_THEME_COLOR':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: {
            ...state.preferences.theme,
            color: action.payload,
            font: state.preferences.theme?.font ?? 'lyon',
            size: state.preferences.theme?.size ?? 'md',
            width: state.preferences.theme?.width ?? 'md',
            wpm: state.preferences.theme?.wpm ?? 350,
          },
        },
      };

    case 'SET_THEME_FONT':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: {
            ...state.preferences.theme,
            color: state.preferences.theme?.color ?? 'light',
            font: action.payload,
            size: state.preferences.theme?.size ?? 'md',
            width: state.preferences.theme?.width ?? 'md',
            wpm: state.preferences.theme?.wpm ?? 350,
          },
        },
      };

    case 'SET_THEME_SIZE':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: {
            ...state.preferences.theme,
            color: state.preferences.theme?.color ?? 'light',
            font: state.preferences.theme?.font ?? 'lyon',
            size: action.payload,
            width: state.preferences.theme?.width ?? 'md',
            wpm: state.preferences.theme?.wpm ?? 350,
          },
        },
      };

    case 'SET_THEME_WIDTH':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: {
            ...state.preferences.theme,
            color: state.preferences.theme?.color ?? 'light',
            font: state.preferences.theme?.font ?? 'lyon',
            size: state.preferences.theme?.size ?? 'md',
            width: action.payload,
            wpm: state.preferences.theme?.wpm ?? 350,
          },
        },
      };

    case 'SET_THEME_WPM':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: {
            ...state.preferences.theme,
            color: state.preferences.theme?.color ?? 'light',
            font: state.preferences.theme?.font ?? 'lyon',
            size: state.preferences.theme?.size ?? 'md',
            width: state.preferences.theme?.width ?? 'md',
            wpm: action.payload,
          },
        },
      };

    case 'SET_SIDEBAR_SECTION_OPEN':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          sidebar: {
            ...state.preferences.sidebar,
            sections: {
              ...state.preferences.sidebar?.sections,
              [action.payload.sectionName]: action.payload.isOpen,
            },
          },
        },
      };

    case 'SET_SHOW_READER_SIDEBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          showReaderSidebar: action.payload,
        },
      };

    case 'SET_READER_SIDEBAR_MODE':
      return {
        ...state,
        ui: {
          ...state.ui,
          readerSidebarMode: action.payload,
          showReaderSidebar: Boolean(action.payload),
        },
      };

    case 'SET_SHOW_MOBILE_SIDEBAR_NAV':
      return {
        ...state,
        ui: {
          ...state.ui,
          showMobileSidebarNav: action.payload,
        },
      };

    case 'TOGGLE_READER_SIDEBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          showReaderSidebar: !state.ui.showReaderSidebar,
        },
      };

    case 'CLOSE_READER_SIDEBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          showReaderSidebar: false,
          readerSidebarMode: null,
        },
      };

    case 'TOGGLE_MOBILE_SIDEBAR_NAV':
      return {
        ...state,
        ui: {
          ...state.ui,
          showMobileSidebarNav: !state.ui.showMobileSidebarNav,
        },
      };

    case 'CLOSE_MOBILE_SIDEBAR_NAV':
      return {
        ...state,
        ui: {
          ...state.ui,
          showMobileSidebarNav: false,
        },
      };

    default:
      return state;
  }
}

// Create context
export const UserPreferencesContext = createContext<
  UserPreferencesContextType | undefined
>(undefined);

// Provider component
interface UserPreferencesProviderProps {
  children: ReactNode;
}

export function UserPreferencesProvider({ children }: UserPreferencesProviderProps) {
  // Get current location to check if we're on a read page
  const location = useLocation();

  // Initialize state from localStorage
  const [state, dispatch] = useReducer(userPreferencesReducer, undefined, () => {
    const savedPreferences = localStorage.getItem('ipt:preferences');

    let preferences: UserPreferences;
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);

        // Validate it has bookmarkList
        if (parsed.bookmarkList) {
          preferences = parsed as UserPreferences;
        } else {
          preferences = {
            bookmarkList: {
              view: 'standard',
              thumbnails: true,
              sortOrder: 'newest',
            },
          };
        }
        // Don't rehydrate the last-opened reader bookmark on fresh load — a
        // hard reload should start with no article in the reader column.
        // Within a session, URL → preference sync repopulates this.
        if (preferences.reader?.bookmarkId) {
          preferences.reader = { ...preferences.reader, bookmarkId: null };
        }
      } catch (error) {
        console.error('Failed to parse user preferences:', error);
        preferences = {
          bookmarkList: {
            view: 'standard',
            thumbnails: true,
            sortOrder: 'newest',
          },
        };
      }
    } else {
      preferences = {
        bookmarkList: {
          view: 'standard',
          thumbnails: true,
          sortOrder: 'newest',
        },
      };
    }

    return {
      preferences,
      ui: {
        showReaderSidebar: false,
        readerSidebarMode: null,
        showMobileSidebarNav: false,
      },
    };
  });

  // Extract values from state for convenience
  const { preferences, ui } = state;

  // Theme values from preferences.theme
  const color = preferences.theme?.color ?? 'light';
  const font = preferences.theme?.font ?? 'lyon';
  const size = preferences.theme?.size ?? 'md';
  const width = preferences.theme?.width ?? 'md';
  const wpm = preferences.theme?.wpm ?? 350;

  // Sidebar preferences
  const sidebarNavPreferences: SidebarNavPreferences = preferences.sidebar?.sections
    ? { sections: preferences.sidebar.sections }
    : { sections: { tags: true, folders: true } };

  // Derived values
  const bookmarkListColumnWidth = preferences.bookmarkList.columnWidth ?? null;
  const readerLayoutMode = preferences.reader?.layout ?? '3col';
  const readerBookmarkId = preferences.reader?.bookmarkId ?? null;
  const showReaderSidebar = ui.showReaderSidebar;
  const readerSidebarMode = ui.readerSidebarMode;
  const showMobileSidebarNav = ui.showMobileSidebarNav;

  // Update body classes based on theme
  const updateBodyClass = useCallback(() => {
    // Check if we're on a read page (including speed read)
    const isReadPage =
      location.pathname.startsWith('/read/') ||
      readerLayoutMode === '3col' ||
      readerLayoutMode === 'full';

    const bodyClasses: string[] = [getThemeBodyClass(color)];

    // Only apply read-specific classes on read pages
    if (isReadPage) {
      bodyClasses.push(
        getFontBodyClass(font),
        getSizeBodyClass(size),
        getWidthBodyClass(width),
      );
    }

    if (preferences.bookmarkList.view === 'condensed') {
      bodyClasses.push('condensed');
    }
    if (!preferences.bookmarkList.thumbnails) {
      bodyClasses.push('nothumbnails');
    }
    // Preserve reader layout class (managed by BookmarkPageContext) so we do not overwrite it
    const hadReadThreeColumn = document.body.classList.contains('read-three-column');
    const hadReadFullWidth = document.body.classList.contains('read-full-width');
    const hadReadListOnly = document.body.classList.contains('read-list-only');
    document.body.className = bodyClasses.join(' ');
    if (hadReadThreeColumn) {
      document.body.classList.add('read-three-column');
    } else if (hadReadFullWidth) {
      document.body.classList.add('read-full-width');
    } else if (hadReadListOnly) {
      document.body.classList.add('read-list-only');
    }
  }, [
    color,
    font,
    size,
    width,
    readerLayoutMode,
    preferences.bookmarkList.view,
    preferences.bookmarkList.thumbnails,
    location.pathname,
  ]);

  // User preferences setters - dispatch wrapper functions
  const setBookmarkListView = (value: 'condensed' | 'standard') => {
    dispatch({ type: 'SET_BOOKMARK_LIST_VIEW', payload: value });
  };

  const setBookmarkListThumbnails = (value: boolean) => {
    dispatch({ type: 'SET_BOOKMARK_LIST_THUMBNAILS', payload: value });
  };

  const setBookmarkListSortOrder = (value: 'newest' | 'oldest') => {
    dispatch({ type: 'SET_BOOKMARK_LIST_SORT_ORDER', payload: value });
  };

  const dismissPremiumNotice = () => {
    dispatch({ type: 'DISMISS_PREMIUM_NOTICE' });
  };

  const setBookmarkListColumnWidth = (width: number | null) => {
    dispatch({ type: 'SET_BOOKMARK_LIST_COLUMN_WIDTH', payload: width });
  };

  const setReaderBookmarkId = useCallback((id: string | null) => {
    dispatch({ type: 'SET_READER_BOOKMARK_ID', payload: id });
  }, []);

  const setReaderLayoutMode = (mode: ReaderLayout) => {
    dispatch({ type: 'SET_READER_LAYOUT_MODE', payload: mode });
  };

  // Theme setters
  const setColor = (newColor: ThemeColor) => {
    dispatch({ type: 'SET_THEME_COLOR', payload: newColor });
  };

  const setFont = (newFont: FontMode) => {
    dispatch({ type: 'SET_THEME_FONT', payload: newFont });
  };

  const setSize = (newSize: ThemeSize) => {
    dispatch({ type: 'SET_THEME_SIZE', payload: newSize });
  };

  const setWidth = (newWidth: WidthMode) => {
    dispatch({ type: 'SET_THEME_WIDTH', payload: newWidth });
  };

  const setWpm = (newWpm: number) => {
    dispatch({ type: 'SET_THEME_WPM', payload: newWpm });
  };

  // Reader sidebar setters
  const setShowReaderSidebar = (show: boolean) => {
    dispatch({ type: 'SET_SHOW_READER_SIDEBAR', payload: show });
  };

  const setReaderSidebarMode = (mode: ReaderSidebarMode | null) => {
    dispatch({ type: 'SET_READER_SIDEBAR_MODE', payload: mode });
  };

  // Reader sidebar utility functions
  const toggleReaderSidebar = () => {
    dispatch({ type: 'TOGGLE_READER_SIDEBAR' });
  };

  const closeReaderSidebar = () => {
    dispatch({ type: 'CLOSE_READER_SIDEBAR' });
  };

  // Mobile nav sidebar functions
  const setShowMobileSidebarNav = (show: boolean) => {
    dispatch({ type: 'SET_SHOW_MOBILE_SIDEBAR_NAV', payload: show });
  };

  const toggleMobileSidebarNav = () => {
    dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR_NAV' });
  };

  const closeMobileSidebarNav = () => {
    dispatch({ type: 'CLOSE_MOBILE_SIDEBAR_NAV' });
  };

  // Sidebar preference setters
  const setSidebarNavSectionOpen = (sectionName: string, isOpen: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_SECTION_OPEN', payload: { sectionName, isOpen } });
  };

  // Update body classes when theme or preferences change
  useEffect(() => {
    updateBodyClass();
  }, [
    color,
    font,
    size,
    width,
    readerLayoutMode,
    preferences.bookmarkList.view,
    preferences.bookmarkList.thumbnails,
    location.pathname,
    updateBodyClass,
  ]);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('ipt:preferences', JSON.stringify(preferences));
  }, [preferences]);

  const value: UserPreferencesContextType = {
    // User preferences
    preferences,
    setBookmarkListView,
    setBookmarkListThumbnails,
    setBookmarkListSortOrder,
    dismissPremiumNotice,

    // Theme settings
    color,
    font,
    size,
    width,
    wpm,
    setColor,
    setFont,
    setSize,
    setWidth,
    setWpm,

    // Reader sidebar management
    showReaderSidebar,
    readerSidebarMode,
    setShowReaderSidebar,
    setReaderSidebarMode,
    toggleReaderSidebar,
    closeReaderSidebar,

    // Mobile navigation sidebar
    showMobileSidebarNav,
    setShowMobileSidebarNav,
    toggleMobileSidebarNav,
    closeMobileSidebarNav,

    // Navigation sidebar preferences
    sidebarNavPreferences,
    setSidebarNavSectionOpen,

    // Bookmark list column width
    bookmarkListColumnWidth,
    setBookmarkListColumnWidth,

    // Last opened reader bookmark id
    readerBookmarkId,
    setReaderBookmarkId,

    // Reader layout mode
    readerLayoutMode,
    setReaderLayoutMode,
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}
